import axiosInstance from "../utils/axios";

const API_ENDPOINTS = {
  GOOGLE_MEET: "api/GoogleMeet",
};

export const createGoogleMeetLink = async (payload) => {
  const { title, description, startTime, endTime, leadId, participants } = payload;

  // Validate required fields
  if (!title || !startTime || !endTime || !leadId) {
    return {
      success: false,
      error: "Title, startTime, endTime, and leadId are required to create Google Meet link",
      data: null,
    };
  }

  // Validate participants array
  if (!participants || !Array.isArray(participants) || participants.length === 0) {
    return {
      success: false,
      error: "At least one participant is required",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.GOOGLE_MEET}/createMeetLink`,
      {
        title,
        description,
        startTime,
        endTime,
        leadId,
        participants,
      }
    );

    const responseData = response.data || {};

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data ?? null,
        message: responseData.message || "Google Meet link created successfully",
      };
    }

    throw new Error(
      responseData.error ||
        responseData.message ||
        "Failed to create Google Meet link"
    );
  } catch (error) {
    console.error("Error creating Google Meet link:", error);

    return {
      success: false,
      error: error.message || "Failed to create Google Meet link",
      data: null,
    };
  }
};

export const getMeetings = async (payload) => {
  const { leadId, pageNumber = 1, pageSize = 10,month,year } = payload;

  // Validate required fields
  if (!leadId) {
    return {
      success: false,
      error: "LeadId is required to fetch Google Meet meetings",
      data: [],
      totalRecords: 0,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.GOOGLE_MEET}/getMettings`,
      {
        leadId,
        pageNumber,
        pageSize,
        month,year
      }
    );

    const responseData = response.data || {};

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let meetings = [];

      // Parse the data string if it's a stringified JSON
      if (typeof responseData.data === "string") {
        try {
          meetings = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing Google Meet meetings:", parseError);
          meetings = [];
        }
      } else if (Array.isArray(responseData.data)) {
        meetings = responseData.data;
      }

      // Transform the meeting data to match our component structure
      const transformedMeetings = meetings.map(meeting => ({
        id: meeting.MeetingId,
        googleEventId: meeting.GoogleEventId,
        meetLink: meeting.MeetLink,
        title: meeting.Title,
        description: meeting.Description,
        startTime: meeting.StartTime,
        endTime: meeting.EndTime,
        leadName: meeting?.LeadName,
        participants: meeting.ParticipantsJson ? JSON.parse(meeting.ParticipantsJson) : [],
        status: meeting.Status === "Active" ? "scheduled" : meeting.Status.toLowerCase(),
        createdAt: meeting.CreatedDate,
      }));

      return {
        success: true,
        data: transformedMeetings,
        totalRecords: responseData.totalRecords ?? transformedMeetings.length,
        message: responseData.message || "Google Meet meetings fetched successfully",
      };
    }

    throw new Error(responseData.message || "Failed to fetch Google Meet meetings");
  } catch (error) {
    console.error("Error fetching Google Meet meetings:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch Google Meet meetings",
      data: [],
      totalRecords: 0,
    };
  }
};

export const updateMeetLink = async (payload) => {
  const { meetingId, eventId, title, description, startTime, endTime, leadId, participants } = payload;

  // Validate required fields
  if (!meetingId || !eventId || !title || !startTime || !endTime || !leadId) {
    return {
      success: false,
      error: "MeetingId, eventId, title, startTime, endTime, and leadId are required to update Google Meet link",
      data: null,
    };
  }

  // Validate participants array
  if (!participants || !Array.isArray(participants) || participants.length === 0) {
    return {
      success: false,
      error: "At least one participant is required",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.GOOGLE_MEET}/updateMeetLink`,
      {
        meetingId,
        eventId,
        title,
        description,
        startTime,
        endTime,
        leadId,
        participants,
      }
    );

    const responseData = response.data || {};

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data ?? null,
        message: responseData.message || "Google Meet link updated successfully",
      };
    }

    throw new Error(
      responseData.error ||
        responseData.message ||
        "Failed to update Google Meet link"
    );
  } catch (error) {
    console.error("Error updating Google Meet link:", error);

    return {
      success: false,
      error: error.message || "Failed to update Google Meet link",
      data: null,
    };
  }
};

export const cancelMeeting = async (payload) => {
  const { meetingId, eventId } = payload;

  // Validate required fields
  if (!meetingId || !eventId) {
    return {
      success: false,
      error: "MeetingId and eventId are required to cancel Google Meet meeting",
      data: null,
    };
  }

  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.GOOGLE_MEET}/cancelMeeting`,
      {
        meetingId,
        eventId,
      }
    );

    const responseData = response.data || {};

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data ?? null,
        message: responseData.message || "Google Meet meeting cancelled successfully",
      };
    }

    throw new Error(
      responseData.error ||
        responseData.message ||
        "Failed to cancel Google Meet meeting"
    );
  } catch (error) {
    console.error("Error cancelling Google Meet meeting:", error);

    return {
      success: false,
      error: error.message || "Failed to cancel Google Meet meeting",
      data: null,
    };
  }
};

